// The code that runs inside the sandbox worker, kept as source text so the
// exact same text runs in the browser worker and in the Node tests (and no
// bundler can rewrite it). Plain ES2017, no imports, no template literals.
//
// runPipeline(job, onStep) runs code steps in order:
//   job: {
//     tables: { [sourceId]: table },
//     steps: [{ id, inputs: [tableId], code, values, disabled }],
//   }
//   table: { columns: [{ name, type }], rows: [{ [name]: value }] }
// Returns { results: [{ stepId, status, rowsIn, rowsOut, durationMs, error?, failedChecks? }], tables: { [stepId]: table } }
// and stops at the first failing step. Each step's code must define
//   function transform({ table, tables, values, helpers }) { return rows | { columns, rows } }
// `helpers` is the library below (HELPERS_SOURCE). Saved step code calls it by
// name, so a helper's behaviour must never change once released: add new
// helpers instead. The list is mirrored in server/ai/codeCheck.js.
export const HELPER_NAMES = [
  "isBlank",
  "text",
  "sameText",
  "parseNumber",
  "parseDate",
  "formatDate",
  "addDays",
  "addMonths",
  "endOfMonth",
  "round",
  "sum",
  "groupBy",
  "unique",
];

// Helpers v1. Dates are local calendar dates, like the dates read from files
// (engine/values.js). Nothing here throws on odd input: unreadable values
// give null, so step code decides what to do with them.
const HELPERS_SOURCE = String.raw`
var MONTH_NAMES = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
var MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function hIsBlank(value) {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "") || (typeof value === "number" && isNaN(value));
}

function pad(number, size) {
  var text = String(number);
  while (text.length < size) text = "0" + text;
  return text;
}

function isValidDate(value) {
  return value instanceof Date && !isNaN(value.getTime());
}

function hText(value) {
  if (hIsBlank(value)) return "";
  if (isValidDate(value)) {
    var day = value.getFullYear() + "-" + pad(value.getMonth() + 1, 2) + "-" + pad(value.getDate(), 2);
    return value.getHours() || value.getMinutes() || value.getSeconds() ? day + " " + pad(value.getHours(), 2) + ":" + pad(value.getMinutes(), 2) : day;
  }
  if (value instanceof Date) return "";
  return String(value).trim();
}

function hSameText(a, b) {
  return hText(a).replace(/\s+/g, " ").toLowerCase() === hText(b).replace(/\s+/g, " ").toLowerCase();
}

var CURRENCY_CODES = "SGD|USD|EUR|GBP|JPY|AUD|CAD|CHF|CNY|RMB|HKD|INR|MYR|NZD|PHP|THB|IDR|KRW|TWD|VND|AED|SAR|ZAR|PKR|BDT|LKR|RM|RS";
var CURRENCY_START = new RegExp("^(?:" + CURRENCY_CODES + ")\\.?", "i");
var CURRENCY_END = new RegExp("(?:" + CURRENCY_CODES + ")$", "i");

// "1,234.50", "(1,234.50)", "-1 234,5", "$ 12", "SGD 1,200", "12%", "1'000".
// One separator followed by exactly three digits ("1,234" or "1.234") is a
// thousands separator only for a comma; "1.234" is one point two three four.
function hParseNumber(value) {
  if (typeof value === "number") return isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  var text = value.replace(/[\s\u00A0\u202F']/g, "");
  if (!text) return null;
  var negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }
  text = text.replace(CURRENCY_START, "").replace(CURRENCY_END, "");
  text = text.replace(/[$€£¥₹%]/g, "");
  if (/^-/.test(text)) {
    negative = !negative;
    text = text.slice(1);
  } else if (/-$/.test(text)) {
    negative = !negative;
    text = text.slice(0, -1);
  } else if (/^\+/.test(text)) text = text.slice(1);
  if (!/^[\d.,]+$/.test(text) || !/\d/.test(text)) return null;
  var lastComma = text.lastIndexOf(",");
  var lastDot = text.lastIndexOf(".");
  if (lastComma !== -1 && lastDot !== -1) {
    var decimal = lastComma > lastDot ? "," : ".";
    var thousands = decimal === "," ? /\./g : /,/g;
    text = text.replace(thousands, "");
    if (decimal === ",") text = text.replace(",", ".");
  } else if (lastComma !== -1) {
    if (/^\d{1,3}(,\d{3})+$/.test(text)) text = text.replace(/,/g, "");
    else if (/^\d+,\d+$/.test(text)) text = text.replace(",", ".");
    else return null;
  } else if (lastDot !== -1 && text.indexOf(".") !== lastDot) {
    if (/^\d{1,3}(\.\d{3})+$/.test(text)) text = text.replace(/\./g, "");
    else return null;
  }
  var number = Number(text);
  if (!isFinite(number)) return null;
  return negative ? -number : number;
}

function makeDate(year, month, day, hours, minutes, seconds) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  var date = new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
  if (year < 100) date.setFullYear(year);
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

function fullYear(year) {
  if (year >= 100) return year;
  return year < 50 ? 2000 + year : 1900 + year;
}

function monthIndex(word) {
  var lower = String(word).toLowerCase().replace(/\.$/, "");
  if (lower.length < 3) return -1;
  for (var i = 0; i < 12; i++) if (MONTH_NAMES[i].indexOf(lower) === 0 || (lower === "sept" && i === 8)) return i;
  return -1;
}

function addTime(date, match, from) {
  if (!date || !match[from]) return date;
  var hours = Number(match[from]);
  var meridiem = (match[from + 3] || "").toLowerCase().charAt(0);
  if (meridiem === "p" && hours < 12) hours += 12;
  if (meridiem === "a" && hours === 12) hours = 0;
  if (hours > 23) return null;
  date.setHours(hours, Number(match[from + 1]), Number(match[from + 2] || 0));
  return date;
}

var TIME = "(?:[T\\s]+(\\d{1,2}):(\\d{2})(?::(\\d{2}))?(?:\\.\\d+)?\\s*([ap]\\.?m\\.?)?)?(?:\\s*(?:Z|[+-]\\d{2}:?\\d{2}))?";
var ISO = new RegExp("^(\\d{4})[-/.](\\d{1,2})[-/.](\\d{1,2})" + TIME + "$", "i");
var NUMERIC = new RegExp("^(\\d{1,2})[-/.](\\d{1,2})[-/.](\\d{2}|\\d{4})" + TIME + "$", "i");
var DAY_FIRST = new RegExp("^(\\d{1,2})(?:st|nd|rd|th)?[\\s\\-/.]*(?:of\\s+)?([A-Za-z]{3,9}\\.?)[\\s\\-/.,]*(\\d{2}|\\d{4})?" + TIME + "$", "i");
var MONTH_FIRST = new RegExp("^([A-Za-z]{3,9}\\.?)[\\s\\-/.]*(\\d{1,2})(?:st|nd|rd|th)?(?:[\\s,]+(\\d{4}))?" + TIME + "$", "i");
var MONTH_YEAR = /^([A-Za-z]{3,9}\.?)[\s\-/.,]*(\d{4}|\d{2})$/i;
var COMPACT = /^(\d{4})(\d{2})(\d{2})$/;

// Dates as written: Date values, ISO (2026-01-31), 31/01/2026 (order from
// options.order "DMY" | "MDY", else guessed when a part is over 12, else
// DMY), 31 Jan 2026, Jan 31, 2026, January 2026 (the 1st), 20260131 and
// Excel serial numbers. A missing year means the current year.
function hParseDate(value, options) {
  if (value instanceof Date) return isValidDate(value) ? new Date(value.getTime()) : null;
  if (typeof value === "number") {
    if (!isFinite(value) || value < 1 || value >= 2958466) return null;
    var whole = Math.floor(value);
    var serial = new Date(1899, 11, 30 + whole);
    var seconds = Math.round((value - whole) * 86400);
    if (seconds) serial.setSeconds(seconds);
    return serial;
  }
  if (typeof value !== "string") return null;
  var text = value.trim().replace(/\s+/g, " ");
  if (!text) return null;
  var order = options && typeof options.order === "string" ? options.order.toUpperCase() : "";
  var match = ISO.exec(text);
  if (match) return addTime(makeDate(Number(match[1]), Number(match[2]), Number(match[3])), match, 4);
  match = COMPACT.exec(text);
  if (match) return makeDate(Number(match[1]), Number(match[2]), Number(match[3]));
  match = NUMERIC.exec(text);
  if (match) {
    var first = Number(match[1]);
    var second = Number(match[2]);
    var monthFirst = order === "MDY" || (order !== "DMY" && second > 12 && first <= 12);
    return addTime(makeDate(fullYear(Number(match[3])), monthFirst ? first : second, monthFirst ? second : first), match, 4);
  }
  var thisYear = new Date().getFullYear();
  match = DAY_FIRST.exec(text);
  if (match && monthIndex(match[2]) !== -1) {
    return addTime(makeDate(match[3] ? fullYear(Number(match[3])) : thisYear, monthIndex(match[2]) + 1, Number(match[1])), match, 4);
  }
  match = MONTH_FIRST.exec(text);
  if (match && monthIndex(match[1]) !== -1) {
    return addTime(makeDate(match[3] ? Number(match[3]) : thisYear, monthIndex(match[1]) + 1, Number(match[2])), match, 4);
  }
  match = MONTH_YEAR.exec(text);
  if (match && monthIndex(match[1]) !== -1) return makeDate(fullYear(Number(match[2])), monthIndex(match[1]) + 1, 1);
  return null;
}

// Tokens: YYYY YY MMMM MMM MM M DD D HH mm ss. Unreadable dates give "".
function hFormatDate(value, pattern) {
  var date = hParseDate(value);
  if (!date) return "";
  var parts = {
    YYYY: String(date.getFullYear()),
    YY: pad(date.getFullYear() % 100, 2),
    MMMM: MONTH_NAMES[date.getMonth()].charAt(0).toUpperCase() + MONTH_NAMES[date.getMonth()].slice(1),
    MMM: MONTH_SHORT[date.getMonth()],
    MM: pad(date.getMonth() + 1, 2),
    M: String(date.getMonth() + 1),
    DD: pad(date.getDate(), 2),
    D: String(date.getDate()),
    HH: pad(date.getHours(), 2),
    mm: pad(date.getMinutes(), 2),
    ss: pad(date.getSeconds(), 2),
  };
  return String(pattern || "YYYY-MM-DD").replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|mm|ss/g, function (token) {
    return parts[token];
  });
}

function hAddDays(value, days) {
  var date = hParseDate(value);
  var count = hParseNumber(days);
  if (!date || count === null) return null;
  date.setDate(date.getDate() + Math.trunc(count));
  return date;
}

// Same day n months later; the 31st becomes the last day of a shorter month.
function hAddMonths(value, months) {
  var date = hParseDate(value);
  var count = hParseNumber(months);
  if (!date || count === null) return null;
  var day = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + Math.trunc(count));
  var last = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(day, last));
  return date;
}

// Last day of the month, monthsAhead months later (0 = the same month).
function hEndOfMonth(value, monthsAhead) {
  var date = hParseDate(value);
  var ahead = monthsAhead === undefined ? 0 : hParseNumber(monthsAhead);
  if (!date || ahead === null) return null;
  return new Date(date.getFullYear(), date.getMonth() + Math.trunc(ahead) + 1, 0);
}

// Half away from zero, without float surprises (round(1.005, 2) is 1.01).
function hRound(value, decimals) {
  var number = hParseNumber(value);
  if (number === null) return null;
  var places = decimals === undefined ? 0 : Math.trunc(hParseNumber(decimals) || 0);
  var sign = number < 0 ? -1 : 1;
  var rounded = Number(Math.round(Number(Math.abs(number) + "e" + places)) + "e" + -places);
  return isFinite(rounded) ? sign * rounded || 0 : sign * Math.abs(number);
}

function keyFunction(key) {
  if (typeof key === "function") return key;
  if (Array.isArray(key)) {
    return function (row) {
      var out = {};
      key.forEach(function (name) {
        out[name] = row[name];
      });
      return out;
    };
  }
  return function (row) {
    return row[key];
  };
}

function keyText(key) {
  if (key && typeof key === "object" && !(key instanceof Date)) {
    return JSON.stringify(Object.keys(key).map(function (name) {
      return hText(key[name]);
    }));
  }
  return hText(key);
}

// Total of a column (or of what fn(row) returns); unreadable values count as 0.
function hSum(rows, column) {
  var read = keyFunction(column);
  var total = (rows || []).reduce(function (sum, row) {
    var number = hParseNumber(read(row));
    return sum + (number === null ? 0 : number);
  }, 0);
  return hRound(total, 10);
}

// Groups in first-seen order: [{ key, rows }]. The key is a column name, a list
// of column names (key is then { name: value }) or fn(row). Keys match by
// their text (trimmed, case kept).
function hGroupBy(rows, key) {
  var read = keyFunction(key);
  var groups = [];
  var index = new Map();
  (rows || []).forEach(function (row) {
    var value = read(row);
    var id = keyText(value);
    if (!index.has(id)) {
      index.set(id, groups.length);
      groups.push({ key: value, rows: [] });
    }
    groups[index.get(id)].rows.push(row);
  });
  return groups;
}

// Rows without repeats (the first is kept), comparing the given columns (a
// name or a list) or every column.
function hUnique(rows, columns) {
  var seen = new Set();
  var names = columns === undefined || columns === null ? null : Array.isArray(columns) ? columns : [columns];
  return (rows || []).filter(function (row) {
    var id = JSON.stringify((names || Object.keys(row)).map(function (name) {
      return hText(row[name]);
    }));
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

var HELPERS = Object.freeze({
  isBlank: hIsBlank,
  text: hText,
  sameText: hSameText,
  parseNumber: hParseNumber,
  parseDate: hParseDate,
  formatDate: hFormatDate,
  addDays: hAddDays,
  addMonths: hAddMonths,
  endOfMonth: hEndOfMonth,
  round: hRound,
  sum: hSum,
  groupBy: hGroupBy,
  unique: hUnique,
});
`;

// Result checks Claude declares with a step (server/ai/codeReply.js
// CHECK_KINDS): run after the step succeeds, reported as pass or fail only.
const CHECKS_SOURCE = String.raw`
function isBlankCell(value) {
  return value === null || value === undefined || value === "";
}

function cellKind(value) {
  if (isBlankCell(value)) return "blank";
  if (value instanceof Date) return "date";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "text";
}

function checkPasses(check, table, inputs) {
  var rows = table.rows;
  var names = table.columns.map(function (c) {
    return c.name;
  });
  var first = inputs[0];
  switch (check.kind) {
    case "notEmpty":
      return rows.length > 0 || !first || first.rows.length === 0;
    case "rowsAtMostInput":
      return !first || rows.length <= first.rows.length;
    case "rowsEqualInput":
      return !first || rows.length === first.rows.length;
    case "hasColumns":
      return (check.columns || []).every(function (name) {
        return names.indexOf(name) !== -1;
      });
    case "columnType":
      if (names.indexOf(check.column) === -1) return false;
      return rows.every(function (row) {
        var kind = cellKind(row[check.column]);
        return kind === "blank" || kind === check.type;
      });
    case "noBlanks":
      if (names.indexOf(check.column) === -1) return false;
      return rows.every(function (row) {
        return !isBlankCell(row[check.column]) && !(typeof row[check.column] === "string" && !row[check.column].trim());
      });
    case "unique":
      var seen = new Set();
      return rows.every(function (row) {
        var id = JSON.stringify((check.columns || []).map(function (name) {
          var value = row[name];
          return value instanceof Date ? value.getTime() : value;
        }));
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    default:
      return true;
  }
}

// Indexes of the checks that failed.
function runChecks(checks, table, inputs) {
  var failed = [];
  (checks || []).forEach(function (check, index) {
    if (!checkPasses(check, table, inputs)) failed.push(index);
  });
  return failed;
}
`;

export const RUNTIME_SOURCE = String.raw`
${HELPERS_SOURCE}
${CHECKS_SOURCE}
var BUILTIN_ERRORS = ["TypeError", "RangeError", "ReferenceError", "SyntaxError", "EvalError", "URIError"];
// Lines Function() puts before the body, plus our "use strict" line.
var WRAPPER_LINES = 3;

// Errors raised by the runtime itself: their messages never contain data.
// Kept in a set step code can't reach (the worker runs inside a function, and
// step code is compiled at global scope), so code can't pass its own error
// off as one of these.
var OWN_ERRORS = new WeakSet();
function ownError(message) {
  var error = new Error(message);
  OWN_ERRORS.add(error);
  return error;
}

function errorLine(error) {
  var match = /<anonymous>:(\d+):\d+/.exec(String(error && error.stack));
  var line = match ? Number(match[1]) - WRAPPER_LINES : null;
  return line && line > 0 ? line : null;
}

function describeError(error) {
  var isError = error instanceof Error;
  var name = isError ? error.name : "Error";
  return {
    name: name,
    message: isError ? String(error.message) : String(error),
    line: isError && !OWN_ERRORS.has(error) ? errorLine(error) : null,
    own: Boolean(isError && OWN_ERRORS.has(error)),
    builtin: BUILTIN_ERRORS.indexOf(name) !== -1,
  };
}

function compile(code) {
  if (typeof code !== "string" || !code.trim()) throw ownError("This step has no code.");
  var transform = new Function('"use strict";\n' + code + '\n;return typeof transform === "function" ? transform : undefined;')();
  if (typeof transform !== "function") throw ownError("The code must define a function named transform.");
  return transform;
}

var TYPE_OF = { number: "number", boolean: "boolean", string: "text" };

function cleanValue(value) {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  var kind = typeof value;
  if (kind === "number") return isFinite(value) ? value : null;
  if (kind === "string" || kind === "boolean") return value;
  if (kind === "bigint") return Number(value);
  if (kind === "object") {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return null;
}

function valueType(value) {
  if (value instanceof Date) return "date";
  return TYPE_OF[typeof value] || "text";
}

// Turns what transform returned into a table with clean values and
// guessed column types (declared types and input types win).
function toTable(output, inputTables) {
  var rows = Array.isArray(output) ? output : output && Array.isArray(output.rows) ? output.rows : null;
  if (!rows) throw ownError("transform must return an array of row objects, or { columns, rows }.");
  for (var r = 0; r < rows.length; r++) {
    var row = rows[r];
    if (!row || typeof row !== "object" || Array.isArray(row)) throw ownError("Every returned row must be an object like { Column: value }.");
  }

  var declared = !Array.isArray(output) && Array.isArray(output.columns) ? output.columns : null;
  var names = [];
  var declaredTypes = {};
  if (declared) {
    declared.forEach(function (column) {
      var name = typeof column === "string" ? column : column && column.name;
      if (typeof name !== "string" || !name) throw ownError("Every column in columns must be a name or { name, type }.");
      if (names.indexOf(name) === -1) names.push(name);
      if (column && column.type) declaredTypes[name] = column.type;
    });
  } else {
    var seen = {};
    rows.forEach(function (row) {
      Object.keys(row).forEach(function (name) {
        if (!seen[name]) {
          seen[name] = true;
          names.push(name);
        }
      });
    });
  }

  var inputTypes = {};
  inputTables.forEach(function (table) {
    table.columns.forEach(function (column) {
      if (column.type && !inputTypes[column.name]) inputTypes[column.name] = column.type;
    });
  });

  var guessed = {};
  var clean = rows.map(function (row) {
    var out = {};
    names.forEach(function (name) {
      var value = cleanValue(row[name]);
      out[name] = value;
      if (value !== null && !guessed[name]) guessed[name] = valueType(value);
    });
    return out;
  });

  var columns = names.map(function (name) {
    var type = declaredTypes[name] || inputTypes[name] || guessed[name];
    return type ? { name: name, type: type } : { name: name };
  });
  return { columns: columns, rows: clean };
}

function copyTable(table) {
  return structuredClone(table);
}

var OBJECT_KEYS = Object.getOwnPropertyNames(Object.prototype);

// After a step failed: runs it again on watched copies of its inputs and
// values, and returns what it read last before failing (usually the cause):
//   { table, column, valueType } or { value, valueType }
// valueType is "text" | "number" | "boolean" | "date" | "blank" | "missing" |
// "other". Only names are returned, never the value itself; the data host
// and the server keep a column only if the inputs or the code name it.
function diagnose(transform, step, inputs) {
  var last = null;
  var watch = function (target, describe) {
    return new Proxy(target, {
      get: function (object, key, receiver) {
        var value = Reflect.get(object, key, receiver);
        if (typeof key === "string" && OBJECT_KEYS.indexOf(key) === -1) {
          var kind = !Object.prototype.hasOwnProperty.call(object, key) ? "missing" : value === null || value === undefined || value === "" ? "blank" : cellKind(value);
          last = describe(key, kind === "date" || kind === "text" || kind === "number" || kind === "boolean" || kind === "blank" || kind === "missing" ? kind : "other");
        }
        return value;
      },
    });
  };
  var copies = inputs.map(function (table, index) {
    var copy = copyTable(table);
    copy.rows = copy.rows.map(function (row) {
      return watch(row, function (column, valueType) {
        return { table: step.inputs[index], column: column, valueType: valueType };
      });
    });
    return copy;
  });
  var values = watch(Object.assign({}, step.values), function (name, valueType) {
    return { value: name, valueType: valueType };
  });
  try {
    transform({ table: copies[0], tables: copies, values: values, helpers: HELPERS });
  } catch (error) {
    return last;
  }
  return null;
}

function now() {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function runPipeline(job, onStep) {
  var tables = Object.assign({}, job.tables);
  var produced = {};
  var results = [];

  for (var i = 0; i < job.steps.length; i++) {
    var step = job.steps[i];
    if (onStep) onStep(step.id);
    var started = now();
    var inputs = step.inputs.map(function (id) {
      return tables[id];
    });
    var result = { stepId: step.id, inputs: step.inputs, warnings: [] };
    results.push(result);

    if (inputs.some(function (table) { return !table; })) {
      result.status = "error";
      result.error = describeError(ownError("Not connected. Link a source to this step."));
      result.durationMs = now() - started;
      break;
    }
    result.rowsIn = inputs.reduce(function (sum, table) {
      return sum + table.rows.length;
    }, 0);

    if (step.disabled) {
      tables[step.id] = inputs[0];
      result.status = "skipped";
    } else {
      var transform = null;
      try {
        transform = compile(step.code);
        var copies = inputs.map(copyTable);
        var output = transform({ table: copies[0], tables: copies, values: Object.assign({}, step.values), helpers: HELPERS });
        tables[step.id] = toTable(output, inputs);
        result.status = "success";
      } catch (error) {
        result.status = "error";
        result.error = describeError(error);
        if (transform && !result.error.own) {
          var detail = diagnose(transform, step, inputs);
          if (detail) result.error.detail = detail;
        }
        result.durationMs = now() - started;
        break;
      }
      if (step.checks && step.checks.length) {
        var failedChecks = runChecks(step.checks, tables[step.id], inputs);
        if (failedChecks.length) result.failedChecks = failedChecks;
      }
    }
    produced[step.id] = tables[step.id];
    result.rowsOut = tables[step.id].rows.length;
    result.durationMs = now() - started;
  }

  return { results: results, tables: produced };
}
`;

// The worker: waits for the MessagePort from the host page, then answers one
// { job } message with { ok, result | error } (and { step } progress notes so
// a timeout can name the step that was running). Every run gets a fresh
// sandbox that is removed afterwards, so nothing a step does outlives its run.
//
// Step code gets no network or storage: these are removed from the worker
// global and its prototypes before anything runs (the iframe's CSP blocks
// network access as well).
const BLOCKED_GLOBALS = [
  "fetch",
  "XMLHttpRequest",
  "WebSocket",
  "WebSocketStream",
  "EventSource",
  "WebTransport",
  "importScripts",
  "Worker",
  "SharedWorker",
  "BroadcastChannel",
  "indexedDB",
  "caches",
  "RTCPeerConnection",
  "RTCDataChannel",
];
// Everything runs inside one function, so nothing of the runtime is a global
// that step code could read or replace.
export const WORKER_SOURCE = `(function () {
${JSON.stringify(BLOCKED_GLOBALS)}.forEach(function (name) {
  for (var target = self; target; target = Object.getPrototypeOf(target)) {
    if (Object.prototype.hasOwnProperty.call(target, name)) {
      try {
        delete target[name];
      } catch (e) {}
    }
  }
  try {
    Object.defineProperty(self, name, { value: undefined, writable: false, configurable: false });
  } catch (e) {}
});
${RUNTIME_SOURCE}
self.onmessage = function (event) {
  var port = event.ports && event.ports[0];
  if (event.data !== "connect" || !port) return;
  self.onmessage = null;
  port.onmessage = function (message) {
    port.onmessage = null; // one job per sandbox
    var job = message.data && message.data.job;
    var reply;
    try {
      var result = runPipeline(job, function (stepId) {
        port.postMessage({ step: stepId });
      });
      reply = { ok: true, result: result };
    } catch (error) {
      reply = { ok: false, error: String((error && error.message) || error) };
    }
    port.postMessage(reply);
  };
};
})();
`;

// The runtime's own error messages (the only ones sent to the AI as written).
export const OWN_MESSAGES = [
  "This step has no code.",
  "The code must define a function named transform.",
  "transform must return an array of row objects, or { columns, rows }.",
  "Every returned row must be an object like { Column: value }.",
  "Every column in columns must be a name or { name, type }.",
  "Not connected. Link a source to this step.",
];
